import React, { useEffect, useState, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import MagneticCard from "../components/MagneticCard";
import { useSearchBar } from "../App";
import {
  Container,
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Chip,
  InputAdornment,
  Stack,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PasswordIcon from "@mui/icons-material/Password";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageIcon from "@mui/icons-material/Image";
import CodeIcon from "@mui/icons-material/Code";
import { blogs } from "../data/blogs";
import { blogArticles } from "../data/blogArticles";
import {
  formatDate,
  getSearchSnippet,
  debounce,
  slugify,
} from "../utils/helpers";
import { updateMetaTags } from "../utils/seo";
import Footer from "../components/Footer";
import AppPromo from "../components/AppPromo";
import BackToWorldButton from "../components/BackToWorldButton";

const shuffle = <T,>(array: T[]): T[] => {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Module-level flag to ensure shuffle happens once per full page lifecycle
let _blogsShuffled = false;

const utilityItems = [
  {
    label: "Formatter Toolkit",
    description: "JSON, XML, HTML, CSS, JS and Base64 tools.",
    path: "/developer-formatter-toolkit",
    icon: <CodeIcon color="primary" />,
  },
  {
    label: "Image Compression",
    description: "Compress, resize, crop and convert images locally.",
    path: "/image-compression-tool",
    icon: <ImageIcon color="primary" />,
  },
  {
    label: "Word Counter",
    description: "Analyze words, readability, keywords and timing.",
    path: "/word-counter",
    icon: <TextFieldsIcon color="primary" />,
  },
  {
    label: "Password Generator",
    description: "Generate strong passwords, passphrases and PINs.",
    path: "/password-generator",
    icon: <PasswordIcon color="primary" />,
  },
  {
    label: "Unit Converter",
    description: "Convert across technical and everyday units fast.",
    path: "/unit-converter",
    icon: <SwapHorizIcon color="primary" />,
  },
  {
    label: "QR Generator",
    description: "Create branded QR codes for links, Wi‑Fi and more.",
    path: "/qr-generator",
    icon: <QrCode2Icon color="primary" />,
  },
];

const Blogs: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  // Shuffle only on first full page load or refresh (module resets on reload).
  const [shuffledBlogs] = useState(() => {
    if (_blogsShuffled) return blogs;
    _blogsShuffled = true;
    return shuffle(blogs);
  });
  const [filteredBlogs, setFilteredBlogs] = useState(shuffledBlogs);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { setSearchBar } = useSearchBar();

  useEffect(() => {
    updateMetaTags({
      title: "Shashi Sharma - Tech Blog & Articles",
      description:
        "Explore my technical blog articles on software engineering, programming, and technology",
      keywords:
        "blog, articles, software engineering, programming, technology, flutter, react",
      canonical: `${window.location.origin}/blogs`,
      ogTitle: "Shashi Sharma - Tech Blog & Articles",
      ogDescription:
        "Explore my technical blog articles on software engineering, programming, and technology",
      ogUrl: `${window.location.origin}/blogs`,
      ogType: "website",
      twitterCard: "summary",
      twitterTitle: "Shashi Sharma - Tech Blog & Articles",
      twitterDescription:
        "Explore my technical blog articles on software engineering, programming, and technology",
    });

    // Keyboard shortcut for search (Ctrl+F or Cmd+F)
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      if (!searchQuery.trim()) {
        setFilteredBlogs(shuffledBlogs);
        return;
      }

      const query = searchQuery.toLowerCase();
      const filtered = blogs.filter((blog) => {
        const article = blogArticles[blog.id];
        const content = article?.markdownContent || "";

        return (
          blog.title.toLowerCase().includes(query) ||
          blog.excerpt.toLowerCase().includes(query) ||
          blog.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          content.toLowerCase().includes(query)
        );
      });

      setFilteredBlogs(filtered);
    }, 300);

    debouncedSearch();
  }, [searchQuery, shuffledBlogs]);

  // Set the search bar in the Header (hidden on mobile)
  useEffect(() => {
    setSearchBar(
      <Box sx={{ display: { xs: "none", sm: "block" }, width: "100%" }}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          placeholder="Search articles... (Ctrl+F)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setSearchQuery("")}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "background.paper",
            },
          }}
        />
      </Box>,
    );

    return () => {
      setSearchBar(null);
    };
  }, [searchQuery, setSearchBar]);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppPromo />
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "primary.main",
            }}
          >
            Blog Articles
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              mb: 2,
            }}
          >
            Exploring Software Engineering & Technology
          </Typography>
          {searchQuery && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              Found {filteredBlogs.length} article
              {filteredBlogs.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 6, flex: 1 }}>
        <Box sx={{ mb: 4.5 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Developer utilities
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quick access to all client-side tools available on this site.
              </Typography>
            </Box>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${utilityItems.length} utilities`}
            />
          </Stack>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              overflowX: "auto",
              pb: 1,
              scrollSnapType: "x proximity",
            }}
          >
            {utilityItems.map((item) => (
              <Card
                key={item.path}
                component={RouterLink}
                to={item.path}
                sx={{
                  minWidth: { xs: 240, sm: 280 },
                  maxWidth: { xs: 240, sm: 280 },
                  textDecoration: "none",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  scrollSnapAlign: "start",
                  flexShrink: 0,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 24px rgba(15, 23, 42, 0.08)",
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack spacing={1.25}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "rgba(145, 189, 58, 0.08)",
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Mobile Search Bar */}
        <Box
          sx={{
            display: { xs: "block", sm: "none" },
            mb: 4,
          }}
        >
          <TextField
            inputRef={searchInputRef}
            fullWidth
            placeholder="Search articles... (Ctrl+F)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchQuery("")}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          <AnimatePresence>
            {filteredBlogs.flatMap((blog, index) => {
              const article = blogArticles[blog.id];
              const snippet =
                searchQuery && article && article.markdownContent
                  ? getSearchSnippet(article.markdownContent, searchQuery, 80)
                  : blog.excerpt;
              const tiles = [] as React.ReactNode[];

              tiles.push(
                <motion.div
                  key={`${blog.id}-card`}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{
                    duration: 0.45,
                    delay: index * 0.055,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  style={{ display: "flex" }}
                >
                  <MagneticCard>
                    <Tilt
                      tiltMaxAngleX={18}
                      tiltMaxAngleY={18}
                      perspective={900}
                      glareEnable
                      glareMaxOpacity={0.18}
                      glareColor="#91bd3a"
                      glarePosition="all"
                      scale={1.05}
                      transitionSpeed={400}
                      style={{ width: "100%", display: "flex" }}
                    >
                      <Card
                        component={RouterLink}
                        to={`/blog/${slugify(blog.title)}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          textDecoration: "none",
                          width: "100%",
                          cursor: "pointer",
                          transition: "box-shadow 0.3s ease",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
                          "&:hover": {
                            boxShadow:
                              "0 24px 48px rgba(0,0,0,0.45), 0 8px 20px rgba(145,189,58,0.18)",
                          },
                        }}
                      >
                        <Box sx={{ overflow: "hidden" }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={blog.coverImage}
                            alt={blog.title}
                            sx={{
                              transition: "transform 0.5s ease",
                              "&:hover": { transform: "scale(1.08)" },
                            }}
                          />
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mb: 1.5,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDate(blog.publishedAt)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              •
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {blog.readTimeMinutes} min read
                            </Typography>
                          </Box>
                          <Typography
                            variant="h6"
                            component="h2"
                            sx={{
                              mb: 1,
                              fontWeight: 600,
                              lineHeight: 1.3,
                            }}
                          >
                            {blog.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {snippet}
                          </Typography>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            {blog.tags.slice(0, 3).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                  backgroundColor: "rgba(145, 189, 58, 0.1)",
                                  color: "primary.main",
                                  fontWeight: 500,
                                }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Tilt>
                  </MagneticCard>
</motion.div>,
              );

              return tiles;
            })}
          </AnimatePresence>
        </Box>

        {filteredBlogs.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
            }}
          >
            <Typography variant="h5" sx={{ mb: 1 }}>
              No articles found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your search query
            </Typography>
          </Box>
        )}
      </Container>

      <Footer />
      <BackToWorldButton />
    </Box>
  );
};

export default Blogs;
